import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { resolveSiteContent } from '../admin/mergeContent';
import { fetchPublicContent } from './contentApi';
import { CONTENT_UPDATED_EVENT } from './contentEvents';
import { hydrateAllContent } from './hydrateContent';
import { STATIC_SITE_DEFAULTS } from './staticDefaults';
import { isPrerenderCrawl } from '../utils/isPrerenderCrawl';

const POLL_MS = 25000;

const ContentContext = createContext({
  ready: false,
  fromApi: false,
  updatedAt: null,
  siteContent: STATIC_SITE_DEFAULTS,
  refresh: async () => {},
  tick: 0,
});

export function useContent() {
  return useContext(ContentContext);
}

/** Subscribe in any page so it re-renders when admin publishes. */
export function useContentSync() {
  const { tick, ready } = useContent();
  return { tick, ready };
}

export function ContentProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [fromApi, setFromApi] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [siteContent, setSiteContent] = useState(STATIC_SITE_DEFAULTS);
  const [tick, setTick] = useState(0);

  const applyContent = useCallback((apiRaw) => {
    const resolved = resolveSiteContent(apiRaw || {});
    const nextUpdatedAt = resolved.updatedAt || null;

    setSiteContent(resolved);
    hydrateAllContent(resolved);
    setFromApi(Boolean(apiRaw?.updatedAt));

    setUpdatedAt((prev) => {
      if (nextUpdatedAt !== prev) {
        setTick((n) => n + 1);
      }
      return nextUpdatedAt;
    });
  }, []);

  const refresh = useCallback(
    async (cacheBust) => {
      try {
        const content = await fetchPublicContent(cacheBust || Date.now());
        applyContent(content);
      } catch {
        /* keep last siteContent — do not reset to empty */
      } finally {
        setReady(true);
      }
    },
    [applyContent]
  );

  useEffect(() => {
    // Prerender: use bundled static defaults only (no API / no poll).
    if (isPrerenderCrawl()) {
      applyContent({});
      setReady(true);
      return undefined;
    }
    refresh();
    return undefined;
  }, [refresh, applyContent]);

  useEffect(() => {
    if (isPrerenderCrawl()) return undefined;

    const onUpdate = (e) => {
      refresh(e?.detail?.updatedAt || Date.now());
    };
    window.addEventListener(CONTENT_UPDATED_EVENT, onUpdate);

    let channel;
    try {
      channel = new BroadcastChannel('databiqs-content');
      channel.onmessage = (msg) => onUpdate({ detail: msg.data });
    } catch {
      /* ignore */
    }

    return () => {
      window.removeEventListener(CONTENT_UPDATED_EVENT, onUpdate);
      channel?.close();
    };
  }, [refresh]);

  useEffect(() => {
    if (isPrerenderCrawl()) return undefined;

    const poll = () => {
      if (document.visibilityState !== 'visible') return;
      refresh(Date.now());
    };
    const id = window.setInterval(poll, POLL_MS);
    const onFocus = () => {
      if (document.visibilityState === 'visible') refresh(Date.now());
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh(Date.now());
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({ ready, fromApi, updatedAt, siteContent, refresh, tick }),
    [ready, fromApi, updatedAt, siteContent, refresh, tick]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}
